import React from 'react';
import { useContext } from 'react';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { TagRelationContext } from '../../../../../context/TagRelationContext';
import {
  DeleteTagRelationResponse,
  DeleteTagRelationVariables,
  TagRelationMutations,
  CreateTagRelationResponse,
  CreateTagRelationVariables,
} from '../../../../../graphql/tagRelation.graphql';
import useMutation from '../../../../../hooks/useMutation';
import { EntityType } from '../../../../../types/comment';
import { TagRelation } from '../../../../../types/tag/tagRelation';
import { toMap } from '../../../../../util/map.util';
import TagMultiSelect from './TagMultiSelect';

interface EntityTagMultiSelectProps {
  entityId: string;
  entityType: EntityType;
}

export default function EntityTagMultiSelect({ entityId, entityType }: EntityTagMultiSelectProps) {
  const { currentCompany } = useContext(CompanyContext);
  const companyId = currentCompany.id;

  const { tagRelations, setTagRelations } = useContext(TagRelationContext);

  const tagRelationsByTagId = toMap(
    [...tagRelations.values()].filter(tr => tr.entityId === entityId),
    'tagId',
  );
  const tagIds = new Set([...tagRelationsByTagId.keys()]);

  const [deleteTagRelation, { loading: deleteTagRelationLoading }] = useMutation<
    DeleteTagRelationResponse,
    DeleteTagRelationVariables
  >(TagRelationMutations.remove, {
    onCompleted: response => {
      tagRelations.delete(response.deleteTagRelation[0].id);
      setTagRelations(new Map(tagRelations));
    },
  });

  const [createTagRelation, { loading: createTagRelationLoading }] = useMutation<
    CreateTagRelationResponse,
    CreateTagRelationVariables
  >(TagRelationMutations.create, {
    onCompleted: response => {
      setTagRelations(new Map(tagRelations).set(response.createTagRelation[0].id, response.createTagRelation[0]));
    },
  });

  return (
    <TagMultiSelect
      entityType={entityType}
      selectedTagIds={tagIds}
      onTagCreated={async tag => {
        await createTagRelation({
          variables: {
            tagRelations: [
              new TagRelation({ companyId }).withEntityId(entityId).withTagId(tag.id).withType(entityType).forCreate(),
            ],
          },
        });
      }}
      onTagRemoved={async tagId => {
        const tagRelation = tagRelationsByTagId.get(tagId);
        if (!tagRelation) return;
        await deleteTagRelation({ variables: { tagRelations: [tagRelation.forDelete()] } });
      }}
      onTagSelected={async tagId => {
        await createTagRelation({
          variables: {
            tagRelations: [
              new TagRelation({ companyId }).withEntityId(entityId).withTagId(tagId).withType(entityType).forCreate(),
            ],
          },
        });
      }}
    />
  );
}
